import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ParentPaper } from '../atoms/parent-paper';
import { SwitchBox } from '../molecules/switch-box';
import { Group } from '../organisms/group';
import { InputBox } from '../molecules/input-box';
import { SliderBox } from '../molecules/slider-box';
import { useSelector } from '../store/utils';
import { Breadcrumbs } from '../atoms/breadcrumbs';
import { SelectBox } from '../molecules/select-box';
import { ValueBox } from '../molecules/value-box';
import { ShelliesTable } from '../molecules/shellies-table';
import { EnergyMetersSettings } from '../organisms/energy-meters-settings';

const EmsService = () => {
  const [
    ems,
    serviceActive,
    mqttBrokerRunning,
    shellies,
    plcStatus,
    distributionMetricsSource,
  ] = useSelector(
    ({ variables }) =>
      [
        variables.EMS,
        variables.SERVICE_ACTIVE,
        variables.MQTT_BROKER_RUNNING,
        variables.MQTT_BROKER_SHELLIES,
        variables.PLC_STATUS,
        variables.DISTRIBUTION_METRICS_SOURCE,
      ] as const
  );

  // EMS feature must be enabled
  if (!ems) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="ems_service.title"
            defaultMessage="EMS Service"
          />
        }
      >
        <ParentPaper>
          <FormattedMessage
            id="emsService.notAvailable"
            defaultMessage="Funkce EMS není k dispozici"
          />
        </ParentPaper>
      </Group>
    );
  }

  // Service password verification
  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="ems_service.title"
            defaultMessage="EMS Service"
          />
        }
      >
        <InputBox
          title={
            <FormattedMessage
              id="expertService.password"
              defaultMessage="Přístup do servisu"
            />
          }
          inputVariableId="SERVICE_PASSWORD"
          label={
            <FormattedMessage
              id="expertService.password.label"
              defaultMessage="Heslo"
            />
          }
          fullWidth
        />
      </Group>
    );
  }

  return (
    <>
      <Group layout="list">
        <Breadcrumbs
          items={[
            {
              label: (
                <FormattedMessage
                  id="ems_service.link"
                  defaultMessage="EMS Service"
                />
              ),
            },
          ]}
        />
      </Group>

      <Group
        title={
          <FormattedMessage
            id="emsService.basicSettings"
            defaultMessage="Komunikace s RTU"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="emsService.enabled"
                defaultMessage="PLC Povoleno"
              />
            }
            switchVariableId="PLC_CONFIG.enabled"
          />
          <ValueBox
            type="row"
            title={
              <FormattedMessage
                id="emsService.plcStatus"
                defaultMessage="Stav PLC komunikace"
              />
            }
            value={
              <>
                {plcStatus?.connected ? (
                  <span style={{ color: 'green' }}>
                    ✓ Připojeno (úspěšných: {plcStatus.communicationSuccess},
                    chyb: {plcStatus.communicationErrors})
                  </span>
                ) : (
                  <span style={{ color: 'red' }}>
                    ✗ Nepřipojeno{' '}
                    {plcStatus?.communicationErrors
                      ? `(chyb: ${plcStatus.communicationErrors})`
                      : ''}
                  </span>
                )}
              </>
            }
          />
          <InputBox
            title={
              <FormattedMessage
                id="emsService.ipAddress"
                defaultMessage="IP Adresa"
              />
            }
            inputVariableId="PLC_CONFIG.ipAddress"
            label={
              <FormattedMessage
                id="emsService.ipAddress"
                defaultMessage="IP Adresa"
              />
            }
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="emsService.username"
                defaultMessage="Uživatelské jméno"
              />
            }
            inputVariableId="PLC_CONFIG.username"
            label={
              <FormattedMessage
                id="emsService.username"
                defaultMessage="Uživatelské jméno"
              />
            }
            fullWidth
          />
          <InputBox
            title={
              <FormattedMessage
                id="emsService.password"
                defaultMessage="Heslo"
              />
            }
            inputVariableId="PLC_CONFIG.password"
            label={
              <FormattedMessage
                id="emsService.password"
                defaultMessage="Heslo"
              />
            }
            type="password"
            fullWidth
          />
          <SliderBox
            title={
              <FormattedMessage
                id="emsService.pollInterval"
                defaultMessage="Interval dotazování (ms)"
              />
            }
            variableId="PLC_CONFIG.pollInterval"
            min={100}
            max={60000}
            step={100}
          />
        </ParentPaper>

        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="emsService.gridMetrics.enabled"
                defaultMessage="Povoleno čtení paty objektu"
              />
            }
            switchVariableId="PLC_CONFIG.gridMetrics.enabled"
          />
          <SelectBox
            title={
              <FormattedMessage
                id="emsService.metricsDirection"
                defaultMessage="Směr"
              />
            }
            selectVariableId="PLC_CONFIG.gridMetrics.direction"
            options={[
              {
                value: 'disabled',
                label: (
                  <FormattedMessage
                    id="emsService.direction.disabled"
                    defaultMessage="Zakázáno"
                  />
                ),
              },
              {
                value: 'read',
                label: (
                  <FormattedMessage
                    id="emsService.direction.read"
                    defaultMessage="Čtení"
                  />
                ),
              },
              {
                value: 'write',
                label: (
                  <FormattedMessage
                    id="emsService.direction.write"
                    defaultMessage="Zápis"
                  />
                ),
              },
            ]}
          />
        </ParentPaper>
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="emsService.pvMetrics.enabled"
                defaultMessage="Povoleno čtení z FVE"
              />
            }
            switchVariableId="PLC_CONFIG.pvMetrics.enabled"
          />
          <SelectBox
            title={
              <FormattedMessage
                id="emsService.metricsDirection"
                defaultMessage="Směr"
              />
            }
            selectVariableId="PLC_CONFIG.pvMetrics.direction"
            options={[
              {
                value: 'disabled',
                label: (
                  <FormattedMessage
                    id="emsService.direction.disabled"
                    defaultMessage="Zakázáno"
                  />
                ),
              },
              {
                value: 'read',
                label: (
                  <FormattedMessage
                    id="emsService.direction.read"
                    defaultMessage="Čtení"
                  />
                ),
              },
              {
                value: 'write',
                label: (
                  <FormattedMessage
                    id="emsService.direction.write"
                    defaultMessage="Zápis"
                  />
                ),
              },
            ]}
          />
        </ParentPaper>
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="emsService.kgjMetrics.enabled"
                defaultMessage="Povoleno čtení z KGJ"
              />
            }
            switchVariableId="PLC_CONFIG.kgjMetrics.enabled"
          />
          <SelectBox
            title={
              <FormattedMessage
                id="emsService.metricsDirection"
                defaultMessage="Směr"
              />
            }
            selectVariableId="PLC_CONFIG.kgjMetrics.direction"
            options={[
              {
                value: 'disabled',
                label: (
                  <FormattedMessage
                    id="emsService.direction.disabled"
                    defaultMessage="Zakázáno"
                  />
                ),
              },
              {
                value: 'read',
                label: (
                  <FormattedMessage
                    id="emsService.direction.read"
                    defaultMessage="Čtení"
                  />
                ),
              },
              {
                value: 'write',
                label: (
                  <FormattedMessage
                    id="emsService.direction.write"
                    defaultMessage="Zápis"
                  />
                ),
              },
            ]}
          />
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="emsService.distributionMetrics"
            defaultMessage="Distribuční pokyny"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SelectBox
            title={
              <FormattedMessage
                id="emsService.distributionMetrics.source"
                defaultMessage="Zdroj distribučních pokynů"
              />
            }
            selectVariableId="DISTRIBUTION_METRICS_SOURCE"
            options={[
              {
                value: 'plc',
                label: (
                  <FormattedMessage
                    id="emsService.distributionMetrics.source.plc"
                    defaultMessage="Externí PLC"
                  />
                ),
              },
              {
                value: 'digital-inputs',
                label: (
                  <FormattedMessage
                    id="emsService.distributionMetrics.source.digitalInputs"
                    defaultMessage="Digitální vstupy"
                  />
                ),
              },
            ]}
          />
          {distributionMetricsSource === 'digital-inputs' && (
            <ValueBox
              type="row"
              title={
                <FormattedMessage
                  id="emsService.distributionMetrics.diMapping"
                  defaultMessage="Mapování vstupů"
                />
              }
              value="DI1 = 0%, DI2 = 30%, DI3 = 60%"
            />
          )}
          {distributionMetricsSource === 'plc' && (
            <>
              <SwitchBox
                title={
                  <FormattedMessage
                    id="emsService.distributionMetrics.enabled"
                    defaultMessage="Povoleno čtení distribučních pokynů"
                  />
                }
                switchVariableId="PLC_CONFIG.distributionMetrics.enabled"
              />
              <SelectBox
                title={
                  <FormattedMessage
                    id="emsService.metricsDirection"
                    defaultMessage="Směr"
                  />
                }
                selectVariableId="PLC_CONFIG.distributionMetrics.direction"
                options={[
                  {
                    value: 'disabled',
                    label: (
                      <FormattedMessage
                        id="emsService.direction.disabled"
                        defaultMessage="Zakázáno"
                      />
                    ),
                  },
                  {
                    value: 'read',
                    label: (
                      <FormattedMessage
                        id="emsService.direction.read"
                        defaultMessage="Čtení"
                      />
                    ),
                  },
                ]}
              />
            </>
          )}
        </ParentPaper>
      </Group>

      <Group
        title={
          <FormattedMessage
            id="emsService.mqttBroker"
            defaultMessage="MQTT Broker (Shellies)"
          />
        }
        layout="list"
      >
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="emsService.mqttBrokerEnabled"
                defaultMessage="MQTT Broker povolen"
              />
            }
            switchVariableId="MQTT_BROKER_ENABLED"
          />
        </ParentPaper>

        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="emsService.mqttBrokerStatus"
              defaultMessage="Stav MQTT Brokeru"
            />
          }
          value={mqttBrokerRunning ? 'Aktivní' : 'Neaktivní'}
        />

        <ShelliesTable
          title={
            <FormattedMessage
              id="emsService.shelliesList"
              defaultMessage="Připojená Shelly zařízení"
            />
          }
        />
      </Group>

      <EnergyMetersSettings />
    </>
  );
};

export default EmsService;
